import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Cookies from "js-cookie";
import config from "../Config";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useSingleFlight } from "../hooks/useSingleFlight";
import "./pages.css";

const QUICK_AMOUNTS = [10, 25, 50, 100, 200];

const BRAND_LABELS = { visa: "VISA", mastercard: "Mastercard", amex: "Amex", discover: "Discover" };
const brandLabel = (b) => BRAND_LABELS[b?.toLowerCase()] ?? b?.toUpperCase() ?? "Card";

/* Stripe Elements appearance — matches our input system */
const STRIPE_APPEARANCE = {
  theme: "stripe",
  variables: {
    colorPrimary: "#2563eb",
    colorBackground: "#ffffff",
    colorText: "#0f172a",
    colorDanger: "#dc2626",
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    spacingUnit: "4px",
    borderRadius: "10px",
    fontSizeBase: "0.95rem",
  },
  rules: {
    ".Label": {
      fontWeight: "500",
      fontSize: "0.85rem",
      color: "#334155",
      marginBottom: "6px",
    },
    ".Input": {
      padding: "10px 12px",
      border: "1.5px solid #e2e8f0",
      boxShadow: "none",
    },
    ".Input:focus": {
      border: "1.5px solid #2563eb",
      boxShadow: "0 0 0 3px rgba(37,99,235,0.12)",
    },
  },
};

/* ── New-card form ──────────────────────────────────────────── */
function NewCardForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [saveMethod, setSaveMethod] = useState(false);
  const [nameOnCard, setNameOnCard] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!nameOnCard.trim()) {
      setErrorMsg("Please enter the name on the card.");
      return;
    }
    setProcessing(true);
    setErrorMsg("");
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // The Payment Element decides for itself which billing fields a method needs,
        // and for cards it asks for none, so the cardholder name has to be collected
        // here and attached at confirm time.
        confirmParams: {
          payment_method_data: { billing_details: { name: nameOnCard.trim() } },
        },
        redirect: "if_required",
      });
      if (error) {
        setErrorMsg(
          error.type === "card_error" || error.type === "validation_error"
            ? error.message
            : "An unexpected error occurred."
        );
      } else if (paymentIntent?.status === "succeeded") {
        const token = Cookies.get("runner_app");
        const res = await authFetch(`${config.AUTH_API}/v1/add-credit?amount=${paymentIntent.amount}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        // The server reports the stored balance, so it is preferred over adding the
        // paid amount locally.
        const result = await res.json().catch(() => ({}));
        if (saveMethod && paymentIntent.payment_method) {
          await authFetch(
            `${config.AUTH_API}/v1/save-payment-method?paymentMethodId=${paymentIntent.payment_method}`,
            { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
          );
        }
        onSuccess({ credited: paymentIntent.amount, ...result });
      }
    } catch {
      setErrorMsg("An unexpected error occurred.");
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="cr-form">
      <div className="cr-field">
        <label className="cr-field-label" htmlFor="cr-name-on-card">Name on card</label>
        <div className="cr-input">
          <input
            id="cr-name-on-card"
            type="text"
            value={nameOnCard}
            onChange={(e) => { setNameOnCard(e.target.value); setErrorMsg(""); }}
            placeholder="Jane Smith"
            autoComplete="cc-name"
            className="cr-input-field"
            required
          />
        </div>
      </div>
      <PaymentElement options={{ layout: "tabs" }} />
      <label className="cr-checkbox">
        <input type="checkbox" checked={saveMethod} onChange={(e) => setSaveMethod(e.target.checked)} />
        <span>Save card for future payments</span>
      </label>
      {errorMsg && <p className="cr-error">{errorMsg}</p>}
      <button type="submit" className="cr-btn cr-btn--primary" disabled={processing || !stripe || !elements}>
        {processing ? "Processing…" : "Pay Now"}
      </button>
    </form>
  );
}

/* ── Saved-card form ────────────────────────────────────────── */
function SavedCardForm({ amount, onSuccess }) {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const runPay = useSingleFlight();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = Cookies.get("runner_app");
        const res = await authFetch(`${config.AUTH_API}/v1/get-payment-methods`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        await HandleResponse(res, navigate);
        const data = await res.json();
        if (!cancelled) setMethods(data.paymentMethods || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handlePay = () => {
    if (!selected) { setErrorMsg("Please select a card."); return; }
    runPay(async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const token = Cookies.get("runner_app");
        const res = await authFetch(
          `${config.AUTH_API}/v1/charge-stored-method?paymentMethodId=${selected}&amount=${amount}&currency=usd`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (res.ok && data.status === "successful") {
          onSuccess({ credited: amount, ...data });
        } else {
          setErrorMsg(data.error || "Payment failed. Please try again.");
        }
      } catch {
        setErrorMsg("Payment failed. Please try again.");
      }
      setLoading(false);
    });
  };

  if (methods.length === 0) {
    return (
      <div className="cr-empty">
        <div className="cr-empty-icon">💳</div>
        <p className="cr-empty-title">No saved cards</p>
        <p className="cr-empty-sub">Switch to <strong>New Card</strong> tab to add one.</p>
      </div>
    );
  }

  return (
    <div className="cr-form">
      <div className="cr-card-list">
        {methods.map((m) => (
          <label key={m.id} className={`cr-card-row ${selected === m.id ? "is-active" : ""}`}>
            <input
              type="radio"
              name="savedCard"
              value={m.id}
              checked={selected === m.id}
              onChange={() => { setSelected(m.id); setErrorMsg(""); }}
              className="cr-card-radio"
            />
            <span className="cr-card-chip">{brandLabel(m.brand)}</span>
            <span className="cr-card-num">•••• {m.last4}</span>
            <span className="cr-card-exp">{m.exp_month}/{m.exp_year}</span>
          </label>
        ))}
      </div>
      {errorMsg && <p className="cr-error">{errorMsg}</p>}
      <button className="cr-btn cr-btn--primary" onClick={handlePay} disabled={loading || !selected}>
        {loading ? "Processing…" : `Pay $${(amount / 100).toFixed(2)}`}
      </button>
    </div>
  );
}

/* ── Main Credit component ──────────────────────────────────── */
export default function Credit() {
  const navigate = useNavigate();
  const [stripePromise, setStripePromise] = useState(null);
  const [balance, setBalance] = useState(null);
  const [step, setStep] = useState("amount");
  const [creditAmount, setCreditAmount] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [payMode, setPayMode] = useState("saved");
  const [clientSecret, setClientSecret] = useState("");
  const [inputError, setInputError] = useState("");
  const [success, setSuccess] = useState(null);
  const runIntent = useSingleFlight();

  useEffect(() => {
    let cancelled = false;
    const token = Cookies.get("runner_app");
    (async () => {
      try {
        const [pkRes, balRes] = await Promise.all([
          authFetch(`${config.AUTH_API}/v1/get-public-key`, { method: "GET", headers: { Authorization: `Bearer ${token}` } }),
          authFetch(`${config.AUTH_API}/v1/get-credit`, { method: "GET", headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cancelled) return;
        const { publishableKey } = await pkRes.json();
        const credit = await balRes.json();
        if (!cancelled) {
          setStripePromise(loadStripe(publishableKey));
          setBalance(credit.balance ?? 0);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleAmountContinue = () => {
    const amt = parseFloat(inputValue);
    if (isNaN(amt) || amt < 3) { setInputError("Minimum top-up is $3.00"); return; }
    setCreditAmount(Math.round(amt * 100));
    setInputError("");
    setStep("method");
  };

  const loadIntent = () => {
    if (clientSecret) return;
    runIntent(async () => {
      const token = Cookies.get("runner_app");
      const res = await authFetch(
        `${config.AUTH_API}/v1/create-payment-intent?amount=${creditAmount}&currency=usd`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      await HandleResponse(res, navigate);
      const { clientSecret: cs } = await res.json();
      setClientSecret(cs);
    });
  };

  const handleSuccess = (result) => {
    const credited = result.credited ?? 0;
    // Prefer the server's balance over a locally computed total, so the figure shown
    // is the stored one rather than one that only matches until the next reload.
    setBalance(result.balance ?? ((balance ?? 0) + credited));
    setSuccess({ credited });
  };

  const handleReset = () => {
    setStep("amount");
    setCreditAmount(null);
    setInputValue("");
    setClientSecret("");
    setSuccess(null);
    setPayMode("saved");
  };

  const stripeOptions = useMemo(
    () => ({ clientSecret, appearance: STRIPE_APPEARANCE }),
    [clientSecret]
  );

  /* ── Success screen ── */
  if (success !== null) {
    return (
      <section className="acct-card">
        <div className="acct-card-header"><h2>Add Credit</h2></div>
        <div className="acct-card-body">
          <div className="cr-success">
            <div className="cr-success-ring">✓</div>
            <h3>Credit Added</h3>
            <p className="cr-success-line">
              <strong>${(success.credited / 100).toFixed(2)}</strong> has been added to your account.
            </p>
            <div className="cr-balance-pill">
              New balance <strong>${(balance / 100).toFixed(2)}</strong>
            </div>
            <button className="cr-btn cr-btn--ghost" onClick={handleReset}>Add more credit</button>
          </div>
        </div>
      </section>
    );
  }

  const amtNum = parseFloat(inputValue);
  const continueLabel = !isNaN(amtNum) && amtNum > 0
    ? `Continue with $${amtNum.toFixed(2)}`
    : "Continue";

  return (
    <section className="acct-card">
      <div className="acct-card-header"><h2>Add Credit</h2></div>
      <div className="acct-card-body">

        {/* ── Balance row ── */}
        <div className="cr-balance">
          <div>
            <div className="cr-balance-label">Account balance</div>
            <div className="cr-balance-amount">
              {balance === null ? "—" : `$${(balance / 100).toFixed(2)}`}
            </div>
          </div>
          <div className="cr-balance-hint">
            Credit is applied automatically to usage charges and never expires.
          </div>
        </div>

        {/* ── Step 1: amount ── */}
        {step === "amount" && (
          <div className="cr-form">
            <div className="cr-field">
              <label className="cr-field-label">Quick amounts</label>
              <div className="cr-chips">
                {QUICK_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    className={`cr-chip ${inputValue === String(amt) ? "is-active" : ""}`}
                    onClick={() => { setInputValue(String(amt)); setInputError(""); }}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
            </div>

            <div className="cr-field">
              <label className="cr-field-label" htmlFor="cr-amount-input">Amount</label>
              <div className={`cr-input ${inputError ? "is-error" : ""}`}>
                <span className="cr-input-prefix">$</span>
                <input
                  id="cr-amount-input"
                  type="text"
                  inputMode="decimal"
                  value={inputValue}
                  onChange={(e) => {
                    if (/^\d*\.?\d{0,2}$/.test(e.target.value)) {
                      setInputValue(e.target.value);
                      setInputError("");
                    }
                  }}
                  placeholder="0.00"
                  className="cr-input-field"
                />
                <span className="cr-input-suffix">USD</span>
              </div>
              {inputError && <p className="cr-error">{inputError}</p>}
              <p className="cr-field-hint">Minimum $3.00. No subscription, charged once.</p>
            </div>

            <button className="cr-btn cr-btn--primary" onClick={handleAmountContinue}>
              {continueLabel}
            </button>
            <p className="cr-footnote">🔒 Secured by Stripe</p>
          </div>
        )}

        {/* ── Step 2: payment method ── */}
        {step === "method" && (
          <div className="cr-form">
            <div className="cr-summary">
              <div>
                <div className="cr-summary-label">You're adding</div>
                <div className="cr-summary-amount">${(creditAmount / 100).toFixed(2)}</div>
              </div>
              <button className="cr-summary-edit" onClick={handleReset}>Change</button>
            </div>

            <div className="cr-field">
              <label className="cr-field-label">Payment method</label>
              <div className="cr-seg">
                <button
                  type="button"
                  className={`cr-seg-btn ${payMode === "saved" ? "is-active" : ""}`}
                  onClick={() => setPayMode("saved")}
                >
                  Saved Card
                </button>
                <button
                  type="button"
                  className={`cr-seg-btn ${payMode === "new" ? "is-active" : ""}`}
                  onClick={() => { setPayMode("new"); loadIntent(); }}
                >
                  New Card
                </button>
              </div>
            </div>

            {payMode === "saved" && (
              <SavedCardForm amount={creditAmount} onSuccess={handleSuccess} />
            )}

            {payMode === "new" && stripePromise && clientSecret && (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <NewCardForm onSuccess={handleSuccess} />
              </Elements>
            )}

            {payMode === "new" && !clientSecret && (
              <div className="cr-loading">Loading payment form…</div>
            )}
          </div>
        )}

      </div>
    </section>
  );
}
